// src/components/layout/Sidebar.jsx
import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserPermissions } from "../../features/authSlice";
import { selectRolePermissionsList } from "../../features/rolePermissionSlice";
import { hasPermission } from "../../utils/authUtils";

import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Collapse,
  Tooltip,
  Badge,
  CircularProgress,
} from "@mui/material";

import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  VpnKey as PermissionIcon,
  SupervisedUserCircle as PlatformIcon,
  ViewModule as ModuleIcon,
  CardMembership as SubscriptionIcon,
  ExpandLess,
  LocationOn as BranchIcon,
  Settings as BranchSettingIcon,
  SignalCellularAlt as SignalIcon,
  SupportAgent as CustomerIcon,
  DesignServices as ServiceIcon,
  Timeline as TimelineIcon,
  ExpandMore, // New icon for Customer
  Assessment as AssessmentIcon,
  ConfirmationNumber as TokenIcon,
} from "@mui/icons-material";

const baseMenuGroups = [
  {
    group: "Main",
    items: [
      // NOTE: dashboard path is finalized at runtime based on user type
      { text: "Dashboard", icon: <DashboardIcon />, path: "/AdminDashboard" },
    ],
  },
  {
    group: "Management",
    items: [
      {
        text: "Company",
        icon: <BusinessIcon />,
        children: [{ text: "All Companies", path: "/companies" }],
      },
      { text: "Branch", icon: <BranchIcon />, path: "/branches" },
      {
        text: "Branch Settings",
        icon: <BranchSettingIcon />,
        path: "/branch-settings",
      },
      { text: "Roles", icon: <SecurityIcon />, path: "/roles" },
      { text: "Company Users", icon: <PeopleIcon />, path: "/company-users" },
      // { text: "Modules", icon: <ModuleIcon />, path: "/modules" },
      { text: "Services", icon: <ServiceIcon />, path: "/services" },
      {
        text: "Platform Users",
        icon: <PlatformIcon />,
        path: "/platform-users",
      },
      {
        text: "Role Permissions",
        icon: <PermissionIcon />,
        path: "/role-permissions",
      },
      {
        text: "Subscriptions",
        icon: <SubscriptionIcon />,
        path: "/subscription-plans",
      },
      { text: "Queue Tokens", icon: <TokenIcon />, path: "/queue-tokens" },
      {
        text: "Customer",
        icon: <CustomerIcon />,
        children: [
          { text: "Management", path: "/customers" },
          { text: "Register Staff", path: "/customer-register" },
          { text: "Feedbacks", path: "/customer-feedbacks" },
        ],
      },
      {
        text: "Reports",
        icon: <AssessmentIcon />,
        children: [{ text: "Staff Performance", path: "/staff-performance" }],
      },
    ],
  },
];

const C = {
  primary: "#6366f1",
  primaryHover: "#4f46e5",
  secondary: "#0f172a",
  bgLight: "#f1f5f9",
  textMain: "#1e293b",
  textMuted: "#64748b",
  white: "#ffffff",
  accent: "#f43f5e",
  border: "#e2e8f0",
  primaryLight: "rgba(99,102,241,0.10)",
  primaryHover2: "rgba(99,102,241,0.07)",
  primarySubHover: "rgba(99,102,241,0.06)",
  dotInactive: "#cbd5e1",
  groupLabel: "#94a3b8",
  footerBg: "rgba(99,102,241,0.06)",
};

function NavItem({
  item,
  isCollapsed,
  openMenus,
  handleToggleMenu,
  mobileOpen,
  handleDrawerToggle,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const _closeMobileDrawer = () => {
    if (mobileOpen) {
      handleDrawerToggle();
    }
  };

  const hasChildren = !!item.children?.length;
  const isActive = !hasChildren && location.pathname === item.path;
  const isChildActive =
    hasChildren && item.children.some((c) => location.pathname === c.path);
  const isOpen = openMenus[item.text];

  const btnSx = {
    minHeight: 42,
    px: isCollapsed ? 1.5 : 2,
    py: 0.75,
    borderRadius: "10px",
    justifyContent: isCollapsed ? "center" : "flex-start",
    position: "relative",
    transition: "background-color 0.2s ease, color 0.2s ease",
    mb: 0.5,
    backgroundColor: isActive || isChildActive ? C.primaryLight : "transparent",
    color: isActive || isChildActive ? C.primary : C.textMuted,
    "&::before": isActive
      ? {
        content: "\"\"",
        position: "absolute",
        left: 0,
        top: "20%",
        height: "60%",
        width: "3px",
        borderRadius: "0 4px 4px 0",
        backgroundColor: C.primary,
      }
      : { content: "\"\"", display: "none" },
    "&:hover": {
      backgroundColor:
        isActive || isChildActive ? C.primaryLight : C.primaryHover2,
      color: C.primary,
      "& .sidebar-icon": { color: C.primary },
    },
    "& .sidebar-icon": {
      color: isActive || isChildActive ? C.primary : C.textMuted,
      fontSize: "1.15rem",
      transition: "color 0.2s ease",
    },
  };

  const button = (
    <ListItemButton
      onClick={() => {
        if (hasChildren) {
          handleToggleMenu(item.text);
        } else {
          navigate(item.path);
          _closeMobileDrawer();
        }
      }}
      sx={btnSx}
    >
      <ListItemIcon
        className="sidebar-icon"
        sx={{
          minWidth: 0,
          mr: isCollapsed ? 0 : 1.75,
          justifyContent: "center",
          color: "inherit",
        }}
      >
        {item.icon}
      </ListItemIcon>

      {!isCollapsed && (
        <>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: "0.85rem",
              fontWeight: isActive || isChildActive ? 600 : 500,
              letterSpacing: "-0.1px",
              lineHeight: 1.3,
              color: "inherit",
            }}
          />
          {hasChildren && (
            <Box
              sx={{ display: "flex", alignItems: "center", color: C.textMuted }}
            >
              {isOpen ? (
                <ExpandLess sx={{ fontSize: "1rem" }} />
              ) : (
                <ExpandMore sx={{ fontSize: "1rem" }} />
              )}
            </Box>
          )}
        </>
      )}
    </ListItemButton>
  );

  if (isCollapsed) {
    return (
      <Tooltip title={item.text} placement="right" arrow>
        <ListItem disablePadding sx={{ display: "block" }}>
          {button}
        </ListItem>
      </Tooltip>
    );
  }

  return (
    <>
      <ListItem disablePadding sx={{ display: "block" }}>
        {button}
      </ListItem>
      {hasChildren && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: 2.5, pr: 0.5, mb: 0.5 }}>
            {item.children.map((child) => {
              const childActive = location.pathname === child.path;
              return (
                <ListItemButton
                  key={child.text}
                  onClick={() => {
                    navigate(child.path);
                    _closeMobileDrawer();
                  }}
                  sx={{
                    borderRadius: "8px",
                    py: 0.65,
                    px: 1.5,
                    mb: 0.25,
                    gap: 1.25,
                    backgroundColor: childActive
                      ? C.primarySubHover
                      : "transparent",
                    color: childActive ? C.primary : C.textMuted,
                    transition: "all 0.15s ease",
                    "&:hover": {
                      backgroundColor: C.primarySubHover,
                      color: C.primary,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      flexShrink: 0,
                      backgroundColor: childActive ? C.primary : C.dotInactive,
                      transition: "background-color 0.15s ease",
                    }}
                  />
                  <ListItemText
                    primary={child.text}
                    primaryTypographyProps={{
                      fontSize: "0.82rem",
                      fontWeight: childActive ? 600 : 400,
                      color: "inherit",
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>
      )}
    </>
  );
}

const permissionMap = {
  Dashboard: "",
  // Management group
  Company: "Company.View",
  "All Companies": "Company.View",
  Branch: "Branch.View",
  "Branch Settings": "BranchSetting.View",
  "Company Users": "CompanyUser.View",
  Modules: "Module.View",
  Services: "Service.View",
  "Platform Users": "PlatformUser.View",
  Roles: "Role.View",
  "Role Permissions": "RolePermission.View",
  Subscriptions: "SubscriptionPlan.View",
  "Queue Tokens": "QueueToken.View",
  Customer: "Customer.View",
  "Customer Management": "Customer.View",
  Management: "Customer.View",
  "Register Staff": "Customer.Add",
  Feedbacks: "CustomerFeedback.View",
  Reports: "Report.View",
  "Staff Performance": "Report.View",
};

// ─── DrawerContent
function DrawerContent({ isCollapsed, mobileOpen, handleDrawerToggle }) {
  const [openMenus, setOpenMenus] = useState({});

  const handleToggleMenu = (text) =>
    setOpenMenus((prev) => ({ ...prev, [text]: !prev[text] }));

  const user = useSelector((state) => state.auth.user);
  const permissions = useSelector(selectUserPermissions);
  const rolePermissions = useSelector(selectRolePermissionsList);
  const isConnected = useSelector(
    (state) => state.signalr?.isConnected || false,
  );
  const dashboardPath = user?.isAdmin
    ? "/AdminDashboard"
    : "/EmployeeDashboard";

  // const navigate = useNavigate();
  const hasAccess = useCallback(
    (text) => {
      // Everyone gets Dashboard
      if (text === "Dashboard") return true;

      const perm = permissionMap[text];
      if (!perm) return false; // Stricter - unknown → deny

      // JWT rolrides if present (future-proof)
      if (user?.rolrides?.length > 0 && Array.isArray(user.rolrides)) {
        const hasRolride = user.rolrides.some((r) => {
          if (typeof r === "string")
            return r.toLowerCase() === text.toLowerCase() || r === perm;
          if (typeof r === "object" && r) {
            const mName = r.ModuleName || r.moduleName || r.module;
            const pKey = r.permissionKey || r.PermissionKey || r.permission;
            const isMatch = mName === text || pKey === perm;
            return isMatch && r.CanView !== false;
          }
          return false;
        });
        if (hasRolride) return true;
      }

      // Primary: JWT permissions (user.permissions array)
      if (hasPermission(permissions, perm)) return true;

      // Fallback: Role permissions from Redux
      const rolePerm = rolePermissions?.find(
        (p) =>
          (p.ModuleName || "").trim() === text ||
          (p.permissionKey || "").trim() === perm,
      );
      return rolePerm?.CanView === true;
    },
    [user, permissions, rolePermissions],
  );

  const filteredMenuGroups = useMemo(
    () =>
      baseMenuGroups
        .map((group) => ({
          ...group,
          items: group.items
            .filter((item) => hasAccess(item.text))
            .map((item) => ({
              ...item,
              path: item.text === "Dashboard" ? dashboardPath : item.path,
              children:
                item.children?.filter((child) => hasAccess(child.text)) || [],
            })),
        }))
        .filter((group) => group.items.length > 0),
    [hasAccess, dashboardPath],
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
      }}
    >
      {/* Brand header */}
      <Box
        sx={{
          px: isCollapsed ? 1 : 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "flex-start",
          gap: 1.5,
          minHeight: 64,
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "10px",
            flexShrink: 0,
            background: "linear-gradient(135deg, #6366f1 0%, #818cf8 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(99,102,241,0.35)",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontSize: "0.8rem",
              lineHeight: 1,
            }}
          >
            Q
          </Typography>
        </Box>
        {!isCollapsed && (
          <Box sx={{ ml: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.9rem",
                  letterSpacing: "0.5px",
                  color: C.textMain,
                  lineHeight: 1.1,
                }}
              >
                QMS
              </Typography>
              <Badge
                variant="dot"
                color={isConnected ? "success" : "error"}
                sx={{
                  "& .MuiBadge-badge": {
                    width: 10,
                    height: 10,
                    minWidth: 10,
                  },
                }}
              >
                <SignalIcon
                  sx={{
                    fontSize: "1rem",
                    color: isConnected ? "success.main" : "error.main",
                  }}
                />
              </Badge>
            </Box>
            <Typography
              sx={{
                fontSize: "0.65rem",
                color: C.textMuted,
                fontWeight: 500,
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              {user?.isAdmin ? "Admin Panel" : "Employee Panel"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: 1.5 }}>
        {filteredMenuGroups.map((group, idx) => (
          <Box key={group.group} sx={{ mb: 0.5 }}>
            {!isCollapsed && (
              <Typography
                sx={{
                  px: 2.5,
                  pt: idx === 0 ? 0 : 1.5,
                  pb: 0.75,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "1.2px",
                  textTransform: "uppercase",
                  color: C.groupLabel,
                  display: "block",
                }}
              >
                {group.group}
              </Typography>
            )}
            {isCollapsed && idx > 0 && (
              <Divider sx={{ mx: 1.5, my: 1, borderColor: C.border }} />
            )}
            <List disablePadding sx={{ px: 1 }}>
              {group.items.map((item) => (
                <NavItem
                  key={item.text}
                  item={item}
                  isCollapsed={isCollapsed}
                  openMenus={openMenus}
                  handleToggleMenu={handleToggleMenu}
                  mobileOpen={mobileOpen}
                  handleDrawerToggle={handleDrawerToggle}
                />
              ))}
            </List>
          </Box>
        ))}
      </Box>

      {/* Footer */}
      {!isCollapsed && (
        <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${C.border}` }}>
          <Box
            sx={{
              backgroundColor: C.footerBg,
              borderRadius: "10px",
              px: 1.5,
              py: 1,
            }}
          >
            <Typography
              sx={{ fontSize: "0.7rem", fontWeight: 600, color: C.primary }}
            >
              Version 2.0.0
            </Typography>
            <Typography
              sx={{ fontSize: "0.68rem", color: C.textMuted, mt: 0.25 }}
            >
              QMS © 2025
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─── Sidebar (exported)
function Sidebar({ mobileOpen, handleDrawerToggle, isCollapsed, drawerWidth }) {
  const paperSx = {
    width: drawerWidth,
    transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflowY: "visible",
    overflowX: "hidden",
    borderRight: "1px solid #f0f4f8",
    backgroundColor: C.white,
    boxShadow: "none",
  };

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: drawerWidth },
        flexShrink: { sm: 0 },
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            ...paperSx,
            width: 260,
            boxShadow: "8px 0 30px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DrawerContent
          isCollapsed={false}
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
        />
      </Drawer>

      {/* Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": paperSx,
        }}
        open
      >
        <DrawerContent
          isCollapsed={isCollapsed}
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
        />
      </Drawer>
    </Box>
  );
}

export default Sidebar;
